import { PaginatedContent, ValueUnitPair } from './common';
import { ICarbonOffsetImpact } from './impact';
import { ZeroDecimalCurrency } from './transactions';

export enum ProgrammeType {
  FLIGHT = 'FLIGHT',
  JOURNEY = 'JOURNEY',
  EMPLOYEE = 'EMPLOYEE',
  EVENT = 'EVENT',
  CORPORATE_FLIGHT = 'CORPORATE_FLIGHT',
}

//commonly used as: ProgrammeType | TransactionsTableType
export enum TransactionsTableType {
  INSIGHTS = 'INSIGHTS',
}

export interface Employee {
  emailAddress: string;
  programmeCode: string;
  createdAt: number;
  contributions: number;
  co2Offset: ICarbonOffsetImpact;
  footprint?: number;
  lastOffsetAt?: number;

  // hidden if DISPLAY_CUSTOMER_EMAIL is false
  numberOfCommittedActions?: number;
  location?: string;
  totalPersonalFootprintKg?: number;
}

export interface EmployeesPage extends PaginatedContent {
  content: Employee[],
}

// impact

export type ImpactValueReporting = {
  value: string;
};

interface ImpactSeries {
  dimensionHeaders: {
    name: string;
  }[];
  metricHeaders: {
    name: string;
    type: string;
  }[];
}

export interface ProjectImpact {
  projectId: string;
  co2Quantity: ICarbonOffsetImpact;
  lastContribution: number;
  numberOfContributions: number;
}

export interface ReportingTooltip {
  co2Kgs: number;
  numberOfContributions: number;
}

export interface ImpactRowReporting {
  dimensionValues: ImpactValueReporting[];
  metricValues: ImpactValueReporting[];
}

export interface ImpactReporting {
  impactSeries: ImpactSeries[];
  projectImpacts: ProjectImpact[];
  rows: ImpactRowReporting[];
}

// footprint

export interface UserActionReporting {
  action: {
    alias: string;
    committedActions: number;
    description: string;
    id: string;
    label: string;
    title: string;
  };
  userEngagements: {
    carbonUserId: string;
    createdAt: number;
    type: string;
  }[];
}

export interface EmissionValue {
  code: string;
  averageEmissions: ValueUnitPair;
  percentage?: number;
}

export interface PersonalFootprintReporting {
  overall: {
    footprintCategories: EmissionValue[];
    numberOfUsersWithFootprints: number;
  };
  regionBreakdown: {
    countryCode: string;
    footprintCategories: EmissionValue[];
    numberOfUsersWithFootprints: number;
  }[];
}

// aviation

interface AviationContent {
  co2AmountKgs: ValueUnitPair;
  numberOfPassengers: number;
  numberOfTransactions: number;
  percentageOfTransactions: number;
}

interface PopularCabinClasses extends AviationContent {
  cabinClass: string;
}

interface PopularDistanceCategories extends AviationContent {
  distanceCategory: string;
}

interface PopularOriginAirports extends AviationContent {
  airportCode: string;
  airportName: string;
}

export interface AviationStats {
  averagePrice: ZeroDecimalCurrency;
  popularCabinClasses: {
    content: PopularCabinClasses[];
  };
  popularDistanceCategories: {
    content: PopularDistanceCategories[]
  };
  popularOriginAirports: {
    content: PopularOriginAirports[];
  };
  totalOffsets: number;
  totalPassengers: number;
}

// flights

export interface FlightsResponse {
  data: {
    content: Flight[];
    totalElements: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  }
}

export interface Flight {
  salesCo2Quantity: number;
  flightActivityDistance: number;
  originAirportCode: string;
  destinationAirportCode: string;
  numPassengers: number;
  travelClass: string;
  referencePrice: Price;
  localPrice: Price;
}

interface Price {
  currency: string;
  symbol: string;
  value: number;
  zeroDecimalCurrency: boolean;
}